/**
 * Editable-offset mapping for one paragraph of a v2 `IndexedStream` (LG-2856).
 *
 * An editable offset counts finalized characters only. Embeds occupy one absolute `XmlText` unit
 * and are not counted. A character is finalized when no `utterance_change` governs it, or the one
 * that does has `final !== false`. An insert that lands on the boundary between two finalized runs
 * uses the absolute index of the embed between them, so the new text is inserted before that embed.
 * `translateSlateTextOp` turns one Slate text op in that coordinate space into absolute edits.
 * A delete whose characters are not contiguous becomes several deletes that skip the gap.
 */

import { MARKER_KINDS } from './constants'
import { IndexedStream, StreamEmbed, indexedOpLength, rangesFromStream } from './incremental'

type ParagraphScan = {
    chars: number[]
    emptyInsertIndex: number | null
}

const utteranceFinalOf = (embed: StreamEmbed): boolean | null => {
    if (embed.kind !== MARKER_KINDS.utteranceChange || typeof embed.final !== 'boolean') {
        return null
    }

    return embed.final
}

const scanParagraph = (stream: IndexedStream, paragraphId: string): ParagraphScan | null => {
    const range = rangesFromStream(stream).find((item) => item.id === paragraphId)
    if (range === undefined) {
        return null
    }

    const contentStart = range.start + 1
    const contentEnd = range.start + range.length
    const chars: number[] = []
    let utteranceFinal: boolean | null = null
    let offset = 0

    for (const op of stream) {
        if (offset >= contentEnd) {
            break
        }

        const length = indexedOpLength(op)
        if (typeof op.insert === 'string') {
            const last = Math.min(offset + length, contentEnd)
            for (let index = Math.max(offset, contentStart); index < last; index += 1) {
                if (utteranceFinal !== false) {
                    chars.push(index)
                }
            }
        } else {
            const final = utteranceFinalOf(op.insert)
            if (final !== null) {
                utteranceFinal = final
            }
        }

        offset += length
    }

    return {
        chars,
        emptyInsertIndex: utteranceFinal === false ? null : contentEnd,
    }
}

/**
 * Number of editable characters in the paragraph, or `null` when `paragraphId` is absent.
 * `0` means the paragraph exists and has no editable characters.
 */
export const editableLength = (stream: IndexedStream, paragraphId: string): number | null => {
    const scan = scanParagraph(stream, paragraphId)

    return scan === null ? null : scan.chars.length
}

/**
 * Absolute `XmlText` index where an insert at `offset` lands.
 * `offset` may be `editableLength` (the position after the last editable character).
 * Returns `null` when the paragraph is absent, `offset` is non-finite or outside `0..length`,
 * or the only slot is provisional.
 */
export const editableInsertIndex = (stream: IndexedStream, paragraphId: string, offset: number): number | null => {
    const scan = scanParagraph(stream, paragraphId)
    if (scan === null) {
        return null
    }

    const { chars } = scan
    const length = chars.length
    if (!Number.isFinite(offset) || offset < 0 || offset > length) {
        return null
    }

    if (length === 0) {
        return scan.emptyInsertIndex
    }

    if (offset === 0) {
        return chars[0]
    }

    return chars[offset - 1] + 1
}

/**
 * Absolute `XmlText` index of the editable character at `offset`.
 * Returns `null` when the paragraph is absent or `offset` is not a character (`offset === length`
 * is the insert gap after the last character).
 */
export const editableCharIndex = (stream: IndexedStream, paragraphId: string, offset: number): number | null => {
    const scan = scanParagraph(stream, paragraphId)
    if (scan === null || !Number.isFinite(offset) || offset < 0 || offset >= scan.chars.length) {
        return null
    }

    return scan.chars[offset]
}

/** One absolute `XmlText` mutation. A delete range never covers an embed. */
export type XmlTextEdit = { index: number; insert: string } | { index: number; delete: number }

/** A Slate text op in editable-offset space. `at` is an index into finalized characters only. */
export type SlateTextOp = { type: 'insert_text'; at: number; text: string } | { type: 'remove_text'; at: number; length: number }

const deleteRuns = (indices: number[]): XmlTextEdit[] => {
    if (indices.length === 0) {
        return []
    }

    const edits: XmlTextEdit[] = []
    let runStart = indices[0]
    let runLength = 1

    for (let cursor = 1; cursor < indices.length; cursor += 1) {
        const index = indices[cursor]
        if (index === runStart + runLength) {
            runLength += 1
            continue
        }

        edits.push({ index: runStart, delete: runLength })
        runStart = index
        runLength = 1
    }

    edits.push({ index: runStart, delete: runLength })

    return edits
}

/**
 * Translate one Slate text op into `XmlText` edits on the paragraph's slice.
 * Returns `[]` when the paragraph is absent, the op is empty, `at` or `length` is non-finite,
 * the range leaves the finalized text, or `op.type` is not `insert_text` or `remove_text`.
 * Does not mutate `stream`. Inserts and deletes are not combined; a replace is two calls.
 */
export const translateSlateTextOp = (stream: IndexedStream, paragraphId: string, op: SlateTextOp): XmlTextEdit[] => {
    if (op.type === 'insert_text') {
        if (op.text === '') {
            return []
        }

        const index = editableInsertIndex(stream, paragraphId, op.at)

        return index === null ? [] : [{ index, insert: op.text }]
    }

    if (op.type === 'remove_text') {
        if (!Number.isFinite(op.at) || !Number.isFinite(op.length) || op.length <= 0 || op.at < 0) {
            return []
        }

        const scan = scanParagraph(stream, paragraphId)
        if (scan === null || op.at + op.length > scan.chars.length) {
            return []
        }

        return deleteRuns(scan.chars.slice(op.at, op.at + op.length))
    }

    const unexpected: never = op

    void unexpected

    return []
}
